import React, { Component } from "react";
import axios from "axios";
import config from "../../config.json";
import AuthService from "../../services/authService";

import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import { Formik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";

class LoginPage extends Component {
    apiEndPoint = config.apiEndPoint + "users/login";
    recaptchaRef = React.createRef();

    schema = Yup.object().shape({
        username: Yup.string()
            .required("Username is required")
            .min(4)
            .max(15),

        password: Yup.string()
            .required("Password is required")
            .min(4)
            .max(15),
    });

    async doSubmit(values, captchaResponse) {
        return axios
            .post(this.apiEndPoint, {
                ...values,
                captchaResponse,
            })
            .then((r) => r.data)
            .then((result) => {
                const token = result?.jwt;

                if (!token) {
                    toast.error("Login failed.");
                    return;
                }

                AuthService.storeJWT(token);
                toast.success("Welcome back, warrior.");

                setTimeout(() => (window.location = "/"), 1200);
            })
            .catch((ex) => {
                toast.error(ex?.response?.data || "Error occurred");
                this.recaptchaRef.current?.reset();
            });
    }

    render() {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                        "radial-gradient(circle at 15% 15%, rgba(57,217,138,0.12), transparent 32%), radial-gradient(circle at 90% 85%, rgba(57,217,138,0.08), transparent 30%), linear-gradient(145deg, #06120A 0%, #08180E 48%, #030805 100%)",
                    px: 2,
                    py: { xs: 5, md: 8 },
                }}
            >
                {/* TOP EMERALD GLOW */}
                <Box
                    sx={{
                        position: "absolute",
                        top: -280,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 750,
                        height: 550,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(57,217,138,0.14) 0%, transparent 70%)",
                        filter: "blur(90px)",
                        pointerEvents: "none",
                    }}
                />

                {/* LEFT DECORATIVE GLOW */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "30%",
                        left: -300,
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(57,217,138,0.09) 0%, transparent 70%)",
                        filter: "blur(110px)",
                        pointerEvents: "none",
                    }}
                />

                {/* RIGHT DECORATIVE GLOW */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: -280,
                        right: -250,
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(57,217,138,0.08) 0%, transparent 70%)",
                        filter: "blur(110px)",
                        pointerEvents: "none",
                    }}
                />

                {/* SUBTLE BACKGROUND GRID */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0.13,
                        pointerEvents: "none",
                        backgroundImage:
                            "linear-gradient(rgba(155,255,196,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(155,255,196,0.07) 1px, transparent 1px)",
                        backgroundSize: "65px 65px",
                        maskImage:
                            "linear-gradient(to bottom, black, transparent 85%)",
                    }}
                />

                {/* TOP ACCENT LINE */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background:
                            "linear-gradient(90deg, transparent, #39D98A, #9BFFC4, #39D98A, transparent)",
                        opacity: 0.85,
                    }}
                />

                <Container
                    maxWidth="sm"
                    sx={{
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <Formik
                        validationSchema={this.schema}
                        initialValues={{ username: "", password: "" }}
                        onSubmit={(values) =>
                            this.doSubmit(
                                values,
                                this.recaptchaRef.current?.getValue()
                            )
                        }
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => (
                            <Paper
                                elevation={0}
                                sx={{
                                    position: "relative",
                                    overflow: "hidden",
                                    borderRadius: { xs: "22px", md: "28px" },
                                    background:
                                        "linear-gradient(150deg, rgba(13,35,22,0.98) 0%, rgba(7,19,12,0.99) 52%, rgba(4,11,7,0.99) 100%)",
                                    border:
                                        "1px solid rgba(57,217,138,0.22)",
                                    boxShadow:
                                        "0 35px 100px rgba(0,0,0,0.65), 0 0 60px rgba(57,217,138,0.045)",
                                }}
                            >
                                {/* TOP GREEN ACCENT */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: "10%",
                                        right: "10%",
                                        height: "2px",
                                        background:
                                            "linear-gradient(90deg, transparent, #39D98A, #9BFFC4, #39D98A, transparent)",
                                    }}
                                />

                                {/* HEADER */}
                                <Box
                                    sx={{
                                        position: "relative",
                                        px: { xs: 3, md: 5 },
                                        pt: { xs: 5, md: 6 },
                                        pb: 4,
                                        textAlign: "center",
                                        background:
                                            "linear-gradient(180deg, rgba(57,217,138,0.075), transparent)",
                                    }}
                                >
                                    {/* LOGO MARK */}
                                    <Box
                                        sx={{
                                            width: 88,
                                            height: 88,
                                            mx: "auto",
                                            mb: 3,
                                            borderRadius: "24px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            position: "relative",
                                            transform: "rotate(-5deg)",
                                            background:
                                                "linear-gradient(145deg, #9BFFC4 0%, #39D98A 48%, #13864F 100%)",
                                            boxShadow:
                                                "0 0 0 6px rgba(57,217,138,0.07), 0 18px 45px rgba(57,217,138,0.17)",
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                inset: "7px",
                                                border: "1px solid rgba(4,25,13,0.35)",
                                                borderRadius: "18px",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 65,
                                                height: 65,
                                                borderRadius: "18px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border:
                                                    "1px solid rgba(4,25,13,0.3)",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#062112",
                                                    fontWeight: 1000,
                                                    fontSize: "27px",
                                                    letterSpacing: "-2px",
                                                    fontFamily: "serif",
                                                    transform: "rotate(5deg)",
                                                }}
                                            >
                                                CC
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Typography
                                        sx={{
                                            color: "#9BFFC4",
                                            fontSize: "10px",
                                            fontWeight: 900,
                                            letterSpacing: {
                                                xs: "3px",
                                                md: "4px",
                                            },
                                            mb: 1.5,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        CRITICAL CONQUER • CLASSIC 5165
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "#F1FFF5",
                                            fontWeight: 950,
                                            fontSize: {
                                                xs: "37px",
                                                md: "47px",
                                            },
                                            lineHeight: 1.05,
                                            letterSpacing: "-1.8px",
                                        }}
                                    >
                                        WELCOME
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "#39D98A",
                                            fontWeight: 950,
                                            fontSize: {
                                                xs: "37px",
                                                md: "47px",
                                            },
                                            lineHeight: 1.05,
                                            letterSpacing: "-1.8px",
                                        }}
                                    >
                                        BACK, WARRIOR.
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "rgba(222,255,233,0.62)",
                                            mt: 2.5,
                                            fontSize: "13px",
                                            lineHeight: 1.9,
                                            maxWidth: 340,
                                            mx: "auto",
                                        }}
                                    >
                                        Your adventure is waiting.
                                        <br />
                                        Sign in and return to the classic realm.
                                    </Typography>

                                    {/* CLASSIC SERVER LABEL */}
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mt: 3,
                                            px: 2,
                                            py: 0.9,
                                            borderRadius: "30px",
                                            border:
                                                "1px solid rgba(57,217,138,0.2)",
                                            background:
                                                "rgba(57,217,138,0.055)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: "50%",
                                                background: "#39D98A",
                                                boxShadow:
                                                    "0 0 12px rgba(57,217,138,0.7)",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: "#B8ECCD",
                                                fontSize: "10px",
                                                fontWeight: 800,
                                                letterSpacing: "1.5px",
                                            }}
                                        >
                                            THE CLASSIC ERA
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* DIVIDER */}
                                <Box
                                    sx={{
                                        mx: { xs: 3, md: 5 },
                                        height: "1px",
                                        background:
                                            "linear-gradient(90deg, transparent, rgba(57,217,138,0.3), transparent)",
                                    }}
                                />

                                {/* FORM */}
                                <Box sx={{ p: { xs: 3, md: 5 } }}>
                                    <form onSubmit={handleSubmit}>
                                        {/* FORM LABEL */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography
                                                sx={{
                                                    color: "#9BFFC4",
                                                    fontSize: "11px",
                                                    fontWeight: 900,
                                                    letterSpacing: "2.5px",
                                                    mb: 0.8,
                                                }}
                                            >
                                                PLAYER ACCESS
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color: "rgba(222,255,233,0.5)",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Enter your account credentials below.
                                            </Typography>
                                        </Box>

                                        {/* USERNAME */}
                                        <TextField
                                            fullWidth
                                            name="username"
                                            label="Username"
                                            value={values.username}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            autoComplete="username"
                                            sx={{
                                                mb: 1,
                                                "& .MuiOutlinedInput-root": {
                                                    color: "#F1FFF5",
                                                    borderRadius: "13px",
                                                    background:
                                                        "rgba(255,255,255,0.025)",
                                                    transition: "0.25s",

                                                    "& fieldset": {
                                                        borderColor:
                                                            "rgba(155,255,196,0.13)",
                                                    },

                                                    "&:hover": {
                                                        background:
                                                            "rgba(57,217,138,0.035)",
                                                    },

                                                    "&:hover fieldset": {
                                                        borderColor:
                                                            "rgba(57,217,138,0.45)",
                                                    },

                                                    "&.Mui-focused": {
                                                        background:
                                                            "rgba(57,217,138,0.045)",
                                                    },

                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#39D98A",
                                                        borderWidth: "1px",
                                                    },
                                                },

                                                "& .MuiInputLabel-root": {
                                                    color:
                                                        "rgba(222,255,233,0.48)",
                                                },

                                                "& .MuiInputLabel-root.Mui-focused": {
                                                    color: "#9BFFC4",
                                                },

                                                "& input": {
                                                    color: "#F1FFF5",
                                                    padding: "17px 14px",
                                                },
                                            }}
                                        />

                                        {errors.username &&
                                            touched.username && (
                                                <Typography
                                                    sx={{
                                                        color: "#FF7777",
                                                        fontSize: "12px",
                                                        mt: 1,
                                                        mb: 2,
                                                        pl: 1,
                                                    }}
                                                >
                                                    {errors.username}
                                                </Typography>
                                            )}

                                        {/* PASSWORD */}
                                        <TextField
                                            fullWidth
                                            type="password"
                                            name="password"
                                            label="Password"
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            autoComplete="current-password"
                                            sx={{
                                                mt:
                                                    errors.username &&
                                                        touched.username
                                                        ? 0
                                                        : 2.5,
                                                mb: 1,
                                                "& .MuiOutlinedInput-root": {
                                                    color: "#F1FFF5",
                                                    borderRadius: "13px",
                                                    background:
                                                        "rgba(255,255,255,0.025)",
                                                    transition: "0.25s",

                                                    "& fieldset": {
                                                        borderColor:
                                                            "rgba(155,255,196,0.13)",
                                                    },

                                                    "&:hover": {
                                                        background:
                                                            "rgba(57,217,138,0.035)",
                                                    },

                                                    "&:hover fieldset": {
                                                        borderColor:
                                                            "rgba(57,217,138,0.45)",
                                                    },

                                                    "&.Mui-focused": {
                                                        background:
                                                            "rgba(57,217,138,0.045)",
                                                    },

                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#39D98A",
                                                        borderWidth: "1px",
                                                    },
                                                },

                                                "& .MuiInputLabel-root": {
                                                    color:
                                                        "rgba(222,255,233,0.48)",
                                                },

                                                "& .MuiInputLabel-root.Mui-focused": {
                                                    color: "#9BFFC4",
                                                },

                                                "& input": {
                                                    color: "#F1FFF5",
                                                    padding: "17px 14px",
                                                },
                                            }}
                                        />

                                        {errors.password &&
                                            touched.password && (
                                                <Typography
                                                    sx={{
                                                        color: "#FF7777",
                                                        fontSize: "12px",
                                                        mt: 1,
                                                        mb: 2,
                                                        pl: 1,
                                                    }}
                                                >
                                                    {errors.password}
                                                </Typography>
                                            )}

                                        {/* CAPTCHA */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                mt: 4,
                                                mb: 4,
                                                overflow: "hidden",
                                                "& > div": {
                                                    maxWidth: "100%",
                                                },
                                            }}
                                        >
                                            <ReCAPTCHA
                                                ref={this.recaptchaRef}
                                                sitekey={
                                                    config.RECAPTCHA_SITE_KEY
                                                }
                                            />
                                        </Box>

                                        {/* LOGIN BUTTON */}
                                        <Button
                                            type="submit"
                                            fullWidth
                                            sx={{
                                                position: "relative",
                                                py: 1.8,
                                                borderRadius: "13px",
                                                fontWeight: 950,
                                                fontSize: "13px",
                                                letterSpacing: "1.5px",
                                                textTransform: "uppercase",
                                                background:
                                                    "linear-gradient(135deg, #9BFFC4 0%, #39D98A 48%, #15985A 100%)",
                                                color: "#062112",
                                                boxShadow:
                                                    "0 12px 30px rgba(57,217,138,0.13)",
                                                transition: "0.25s",

                                                "&:hover": {
                                                    background:
                                                        "linear-gradient(135deg, #C4FFDA 0%, #70F0A8 48%, #39D98A 100%)",
                                                    boxShadow:
                                                        "0 16px 40px rgba(57,217,138,0.24)",
                                                    transform:
                                                        "translateY(-2px)",
                                                },

                                                "&:active": {
                                                    transform:
                                                        "translateY(0)",
                                                },
                                            }}
                                        >
                                            Enter the Realm
                                        </Button>

                                        {/* REGISTER */}
                                        <Typography
                                            sx={{
                                                textAlign: "center",
                                                mt: 4,
                                                color:
                                                    "rgba(222,255,233,0.5)",
                                                fontSize: "13px",
                                            }}
                                        >
                                            New to Critical Conquer?{" "}
                                            <a
                                                href="/user/register"
                                                style={{
                                                    color: "#9BFFC4",
                                                    fontWeight: 800,
                                                    textDecoration: "none",
                                                }}
                                            >
                                                Create an account
                                            </a>
                                        </Typography>
                                    </form>
                                </Box>

                                {/* BOTTOM BRANDING */}
                                <Box
                                    sx={{
                                        px: 3,
                                        py: 2.5,
                                        textAlign: "center",
                                        borderTop:
                                            "1px solid rgba(155,255,196,0.08)",
                                        background:
                                            "rgba(0,0,0,0.2)",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color:
                                                "rgba(155,255,196,0.42)",
                                            fontSize: "10px",
                                            fontWeight: 800,
                                            letterSpacing: "2.5px",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        CLASSIC CONQUER • VERSION 5165
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color:
                                                "rgba(222,255,233,0.23)",
                                            fontSize: "10px",
                                            mt: 1,
                                        }}
                                    >
                                        Return to the world you remember.
                                    </Typography>
                                </Box>
                            </Paper>
                        )}
                    </Formik>

                    {/* PAGE FOOTER */}
                    <Typography
                        sx={{
                            mt: 3,
                            textAlign: "center",
                            color: "rgba(222,255,233,0.28)",
                            fontSize: "10px",
                            letterSpacing: "1.5px",
                        }}
                    >
                        YOUR LEGEND. YOUR REALM. YOUR ADVENTURE.
                    </Typography>
                </Container>
            </Box>
        );
    }
}

export default LoginPage;